import { useCallback, useEffect, useMemo } from "react";
import { AdditionalErrorFields } from "src/components/core/AddressForm/AddressForm";
import { AddressModel } from "src/components/core/AddressForm/utils";
import {
  setAddress,
  toggleSameAsBillingAddress,
  failAddressLineValidation,
} from "src/utils/redux/slices/checkoutPhysicalAddress/checkoutPhysicalAddress";
import { typedKeys } from "src/utils/typeWrappers";
import { isValidZipCode } from "src/utils/validate";
import {
  useAppDispatch,
  useCheckout,
  useCheckoutPhysicalAddress,
  useCoreState,
  useCoreStore,
} from "../redux";
import { failShippingLocationValidation } from "src/utils/redux/slices/checkoutPhysicalAddress/action-creators";
import i18n from "src/locales";
import { BillingAddress, paymentTypes } from "src/utils/redux/slices/checkout";
import { useSalesTax } from "../sales-tax";
import {
  acceptedNoomClinicalOffer,
  isSelectedMedPlanWithMedication,
  getSelectedMedPlanType,
  isHRTSurvey,
  isCrossSellShopCheckout,
  isFlowEligibleForHRTProducts,
} from "src/utils/userSegment/features";
import { isBrandedAsyncNoomPlanId } from "src/utils/plans";
import getStore from "src/utils/redux/store";
import { selectResolvedPhysicalAddress } from "src/utils/redux/slices/checkoutPhysicalAddress/address-selectors";
import zipState from "zip-state";
import { requestAddressValidation } from "src/utils/redux/slices/checkoutPhysicalAddress/address-thunks";
import { getFullName } from "src/utils/name";
import {
  BRANDED_VARIANT,
  getEligibleBrandedVariants,
  getEligibleMedProducts,
  NamedEligibilityCheck,
} from "@utils/medEligibility";
import {
  MedEligibleCountries,
  MhtEligibleCountries,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { isFreemiumHealthRoute } from "src/utils/userSegment";
import { validateShopProductShippingLocationEligibility } from "src/utils/shopProducts";

export const MAX_ADDRESS_LINE_LENGTH = 35;

export function requiresPhysicalAddress(state = getStore().getState()) {
  return (
    acceptedNoomClinicalOffer(state) ||
    isHRTSurvey() ||
    isCrossSellShopCheckout()
  );
}

function requiresStrictAddressValidation(state = getStore().getState()) {
  return (
    (isSelectedMedPlanWithMedication() && acceptedNoomClinicalOffer(state)) ||
    isHRTSurvey()
  );
}

interface UsePhysicalAddressFormOptions {
  /** Whether to validate the physical address form. If not provided, uses built-in business logic. */
  validatePhysicalAddressForm?: boolean;
  /** Array of field names to skip validation for. */
  disabledFields?: (keyof AddressModel)[];
}

export function usePhysicalAddressForm(
  options: UsePhysicalAddressFormOptions = {}
) {
  const store = useCoreStore();
  const {
    address,
    isSameAsBillingAddress,
    addressInvalid,
    suggestedAddress,
    chosenModalAddress,
    acceptedSuggestedAddress,
    shouldUpdateFormAddress,
    isAddressLineInvalid,
  } = useCheckoutPhysicalAddress();
  const checkout = useCheckout();

  const coreState = useCoreState();
  const dispatch = useAppDispatch();
  const { calculateSalesTaxDebounced } = useSalesTax();

  const updatePhysicalAddress = useCallback(
    (newAddr: BillingAddress) => {
      const newName = getFullName(newAddr.firstName, newAddr.lastName);

      const addr = {
        ...newAddr,
        ...(newName && {
          name: newName,
        }),
      };

      const { region, zipcode } = addr;
      dispatch(setAddress(addr));
      if (
        (region && address?.region !== region) ||
        (zipcode && address?.zipcode !== zipcode)
      ) {
        calculateSalesTaxDebounced();
      }
    },
    [dispatch, address, calculateSalesTaxDebounced]
  );

  const togglePhysicalAddressSameAsBilling = useCallback(
    (sameAsBillingAddress: boolean) => {
      dispatch(toggleSameAsBillingAddress(sameAsBillingAddress));
      calculateSalesTaxDebounced();
    },
    [dispatch, calculateSalesTaxDebounced]
  );

  useEffect(() => {
    if (checkout.paymentType !== paymentTypes.CREDIT_CARD) {
      togglePhysicalAddressSameAsBilling(false);
    }
  }, [checkout.paymentType, togglePhysicalAddressSameAsBilling]);

  const shouldShowSameAsBillingAddressToggle = useMemo(() => {
    return (
      checkout.paymentType === paymentTypes.CREDIT_CARD && !checkout.walletPay
    );
  }, [checkout.paymentType, checkout.walletPay]);

  function getAddressLineValidation(
    validate = requiresStrictAddressValidation()
  ): AdditionalErrorFields {
    if (validate) {
      let isAddressInvalid = false;
      const { address1, address2 } = selectResolvedPhysicalAddress(coreState);

      if (
        address1?.length > MAX_ADDRESS_LINE_LENGTH &&
        address2?.length > MAX_ADDRESS_LINE_LENGTH
      ) {
        isAddressInvalid = true;
      } else {
        const { addressLine1, addressLine2 } = getSplitAddressLine({
          address1,
          address2,
        });

        if (
          addressLine1.length > MAX_ADDRESS_LINE_LENGTH ||
          addressLine2.length > MAX_ADDRESS_LINE_LENGTH
        ) {
          isAddressInvalid = true;
        }
      }

      if (isAddressInvalid) {
        dispatch(failAddressLineValidation());

        return {
          address1: {
            type: "tooLong",
            message: i18n.t("payment:homeAddressErrors:tooLong"),
          },
        };
      }
    }

    return {};
  }

  function getZipCodeStateEligibleValidation(
    validate = acceptedNoomClinicalOffer() ||
      isHRTSurvey() ||
      isCrossSellShopCheckout(),
    selectedMedPlanType = getSelectedMedPlanType(),
    runEligibilityChecks = true
  ) {
    const enteredAddress = selectResolvedPhysicalAddress(coreState);
    const { zipcode, country } = enteredAddress;

    if (!zipcode) {
      return [];
    }
    const state = zipState(zipcode);

    if (validate) {
      if (isCrossSellShopCheckout()) {
        const ineligibleField = validateShopProductShippingLocationEligibility(
          country,
          state
        );
        if (ineligibleField) {
          dispatch(failShippingLocationValidation());
          return [ineligibleField];
        }
        return [];
      }

      const eligibleCountries = isFlowEligibleForHRTProducts()
        ? MhtEligibleCountries
        : MedEligibleCountries;
      if (!(country in eligibleCountries)) {
        dispatch(failShippingLocationValidation());
        return ["country"];
      }

      const freemiumHealthRoute = isFreemiumHealthRoute();
      const eligibilityOptions = runEligibilityChecks
        ? {
            stateOverwrite: state,
            checkBMI: true,
            checkHealthRisks: true,
            checkState: true,
            checkFlow: true,
            requirePreviousMedicationsAnswer: !freemiumHealthRoute,
            requireHighestBMIAnswer: !freemiumHealthRoute,
            disabledNamedChecks: [
              "isNotEligibleForCompoundedSemaglutide",
            ] as NamedEligibilityCheck[],
          }
        : {
            stateOverwrite: state,
            checkBMI: false,
            checkHealthRisks: false,
            checkState: true,
            checkFlow: false,
            requirePreviousMedicationsAnswer: false,
            requireHighestBMIAnswer: false,
            disabledNamedChecks: [
              "isNotEligibleForCompoundedSemaglutide",
              "isQualifiedForPD",
            ] as NamedEligibilityCheck[],
          };

      const eligibleProducts = getEligibleMedProducts(eligibilityOptions);

      if (!eligibleProducts.includes(selectedMedPlanType)) {
        dispatch(failShippingLocationValidation());
        return ["postcode"];
      }

      // Branded async is a variant of branded that has additional ineligible states
      // so we need to check if the plan is branded async and if it is,
      // we need to check if the shipping location is ineligible.
      const cartPlanId =
        getStore().getState().checkoutOrders?.saleItem?.legacyNoomPlanId;
      if (
        isBrandedAsyncNoomPlanId(cartPlanId) &&
        !getEligibleBrandedVariants(state)?.includes(BRANDED_VARIANT.ASYNC)
      ) {
        dispatch(failShippingLocationValidation());
        return ["postcode"];
      }
    }
    return [];
  }

  function enteredAddressMatchesChosenAddress() {
    if (Object.keys(chosenModalAddress).length > 0) {
      const enteredAddress = selectResolvedPhysicalAddress(coreState);
      return !Object.keys(chosenModalAddress).some(
        (addressField) =>
          chosenModalAddress[addressField] !== enteredAddress[addressField]
      );
    }
    return false;
  }

  async function getPhysicalAddressValidation() {
    if (acceptedSuggestedAddress && enteredAddressMatchesChosenAddress()) {
      return [];
    }

    try {
      await dispatch(requestAddressValidation({})).unwrap();
    } catch {
      return ["invalidPhysicalAddress"];
    }
    return [];
  }

  // Original validation logic - use override if provided, otherwise use built-in logic
  const validatePhysicalAddressForm =
    options.validatePhysicalAddressForm !== undefined
      ? options.validatePhysicalAddressForm
      : requiresPhysicalAddress(store.getState()) && !isSameAsBillingAddress;

  const validateForm = useCallback((): AdditionalErrorFields => {
    if (!validatePhysicalAddressForm) {
      return null;
    }

    const disabledFields = options.disabledFields || [];

    const baseValidation = {
      address1:
        !disabledFields.includes("address1") && !address.address1 ? {} : null,
      city: !disabledFields.includes("city") && !address.city ? {} : null,
      region: !disabledFields.includes("region") && !address.region ? {} : null,
      postcode:
        !disabledFields.includes("postcode") &&
        !isValidZipCode(address.zipcode, address.country)
          ? {}
          : null,
      country:
        !disabledFields.includes("country") && !address.country ? {} : null,
      firstName:
        !disabledFields.includes("firstName") && !address.firstName ? {} : null,
      lastName:
        !disabledFields.includes("lastName") && !address.lastName ? {} : null,
    };

    return {
      ...baseValidation,
      ...getTrErrorState({ address, suggestedAddress, addressInvalid }),
      ...getAddressLineErrorState({ isAddressLineInvalid }),
    };
  }, [
    address,
    addressInvalid,
    suggestedAddress,
    validatePhysicalAddressForm,
    isAddressLineInvalid,
    options.disabledFields,
  ]);

  const hasPhysicalAddressErrors = useCallback(() => {
    const errors = validateForm();
    return errors && typedKeys(errors).some((key) => errors[key]);
  }, [validateForm]);

  return {
    updatePhysicalAddress,
    address,
    isSameAsBillingAddress,
    togglePhysicalAddressSameAsBilling,
    validateForm,
    hasPhysicalAddressErrors,
    shouldShowSameAsBillingAddressToggle,
    getAddressLineValidation,
    getZipCodeStateEligibleValidation,
    getPhysicalAddressValidation,
    acceptedSuggestedAddress,
    shouldUpdateFormAddress,
  };
}

export function getTrErrorState({
  address,
  suggestedAddress,
  addressInvalid,
}: {
  address: BillingAddress;
  suggestedAddress: Partial<BillingAddress>;
  addressInvalid: boolean;
}): AdditionalErrorFields {
  // TR validation request didn't return invalid, or they already started correcting
  if (!addressInvalid) {
    return null;
  }
  // No suggestions available; give them a generic invalid message
  if (!suggestedAddress || Object.keys(suggestedAddress).length === 0) {
    return {
      address1: {
        type: "notFound",
        message: i18n.t("payment:homeAddressErrors:notFound"),
      },
    };
  }
  // Otherwise, give them a message for the fields that differ
  const keys = ["address1", "city", "region", "zipcode"];
  const ret: AdditionalErrorFields = {};
  keys.forEach((key) => {
    if (suggestedAddress[key] && suggestedAddress[key] !== address[key]) {
      const errorKey = key === "zipcode" ? "postcode" : key;
      ret[errorKey] = {
        type: "notRecognized",
        message: i18n.t("payment:homeAddressErrors:notRecognized"),
      };
    }
  });
  return ret;
}

function getAddressLineErrorState({
  isAddressLineInvalid,
}: {
  isAddressLineInvalid: boolean;
}): AdditionalErrorFields {
  // AddressLineValidation didn't return invalid
  if (!isAddressLineInvalid) {
    return null;
  }

  return {
    address1: {
      type: "tooLong",
      message: i18n.t("payment:homeAddressErrors:tooLong"),
    },
  };
}

/**
 * Split the address line based on max length limitation
 */
export function getSplitAddressLine({
  address1 = "",
  address2 = "",
}: {
  address1: string;
  address2?: string;
}) {
  if (
    address1.length < MAX_ADDRESS_LINE_LENGTH &&
    address2?.length < MAX_ADDRESS_LINE_LENGTH
  ) {
    return {
      addressLine1: address1,
      addressLine2: address2,
    };
  }

  const SPLIT_REGEX = /[" ,"]/g;
  const REPLACE_REGEX = /^[, ]+|[, ]+$/g;
  const addressLine = address2 ? address1.concat(", ", address2) : address1;

  if (addressLine.search(SPLIT_REGEX) === -1) {
    return {
      addressLine1: address1,
      addressLine2: address2,
    };
  }

  const matchArray = [...addressLine.matchAll(SPLIT_REGEX)].filter(
    ({ index }) => index <= MAX_ADDRESS_LINE_LENGTH
  );
  const { index } = matchArray[matchArray.length - 1];

  const addressLine1 = addressLine
    .substring(0, index)
    .replace(REPLACE_REGEX, "");
  const addressLine2 = addressLine.substring(index).replace(REPLACE_REGEX, "");

  return {
    addressLine1,
    addressLine2,
  };
}
